import React from 'react';

import { SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import Input from 'components/lib/Input';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { SelectAdvancedFilterOptionType } from 'types/common';

import {
  ACTION_PLAN_ALERT_TYPE_FILTER_OPTIONS,
  ACTION_PLAN_SOURCE_FILTER_OPTIONS_BY_TAB,
  ACTION_PLAN_STATUS_OPTIONS,
  ACTION_PLAN_TAB_DESCRIPTION,
  ActionPlanSourceFilterValue,
  ActionPlanTab,
} from './constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SearchContainer = styled.div`
  width: 500px;
`;

const TabDescription = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  color: var(--active-blue);
  background: none;
  font-family: inherit;
  font-size: 10px;
  cursor: pointer;
`;

const StyledFilter = styled(SelectAdvancedFilter)``;

export type ActionPlansFiltersState = {
  search: string;
  sourceCategories: ActionPlanSourceFilterValue[];
  alertTypes: number[];
  focusAreas: number[];
  owners: number[];
  statuses: number[];
};

type Props = {
  activeTab: ActionPlanTab;
  search: string;
  sourceCategories: ActionPlanSourceFilterValue[];
  alertTypes: number[];
  focusAreas: number[];
  owners: number[];
  statuses: number[];
  focusAreaOptions: SelectAdvancedFilterOptionType[];
  ownerOptions: SelectAdvancedFilterOptionType[];
  onSearchChange: (value: string) => void;
  onSourceCategoriesChange: (values: ActionPlanSourceFilterValue[]) => void;
  onAlertTypesChange: (values: number[]) => void;
  onFocusAreasChange: (values: number[]) => void;
  onOwnersChange: (values: number[]) => void;
  onStatusesChange: (values: number[]) => void;
  onReset: () => void;
};

const ActionPlansFilters = ({
  activeTab,
  search,
  sourceCategories,
  alertTypes,
  focusAreas,
  owners,
  statuses,
  focusAreaOptions,
  ownerOptions,
  onSearchChange,
  onSourceCategoriesChange,
  onAlertTypesChange,
  onFocusAreasChange,
  onOwnersChange,
  onStatusesChange,
  onReset,
}: Props) => {
  const sourceCategoryOptions = ACTION_PLAN_SOURCE_FILTER_OPTIONS_BY_TAB[activeTab];
  const showAlertTypeFilter = activeTab === ACTION_PLAN_SOURCE.ALERTS;

  return (
    <Container data-test-id="action-plans-filters">
      <TabDescription data-test-id="action-plans-tab-description">
        {ACTION_PLAN_TAB_DESCRIPTION[activeTab]}
      </TabDescription>
      <SearchContainer>
        <Input
          data-test-id="action-plans-search-input"
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
          placeholder="Search by plan name"
          prefix={<SearchOutlined />}
        />
      </SearchContainer>
      <FiltersRow>
        {sourceCategoryOptions && (
          <StyledFilter
            className={classNames({ active: sourceCategories.length > 0 })}
            options={sourceCategoryOptions}
            defaultValue={sourceCategories}
            onSelect={(values: SelectAdvancedFilterOptionType[]) =>
              onSourceCategoriesChange(values.map((item) => item.value as ActionPlanSourceFilterValue))
            }
            placeholder="Source"
            dataTestId="action-plans-filter-source"
          />
        )}
        {showAlertTypeFilter && (
          <StyledFilter
            className={classNames({ active: alertTypes.length > 0 })}
            options={ACTION_PLAN_ALERT_TYPE_FILTER_OPTIONS}
            defaultValue={alertTypes}
            onSelect={(values: SelectAdvancedFilterOptionType[]) =>
              onAlertTypesChange(values.map((item) => Number(item.value)))
            }
            placeholder="Alert Type"
            dataTestId="action-plans-filter-alert-type"
          />
        )}
        {focusAreaOptions.length > 0 && (
          <StyledFilter
            className={classNames({ active: focusAreas.length > 0 })}
            options={focusAreaOptions}
            defaultValue={focusAreas}
            onSelect={(values: SelectAdvancedFilterOptionType[]) =>
              onFocusAreasChange(values.map((item) => Number(item.value)))
            }
            placeholder="Focus Area"
            dataTestId="action-plans-filter-focus-area"
          />
        )}
        {ownerOptions.length > 0 && (
          <StyledFilter
            className={classNames({ active: owners.length > 0 })}
            options={ownerOptions}
            defaultValue={owners}
            onSelect={(values: SelectAdvancedFilterOptionType[]) =>
              onOwnersChange(values.map((item) => Number(item.value)))
            }
            placeholder="Owner"
            dataTestId="action-plans-filter-owner"
          />
        )}
        <StyledFilter
          className={classNames({ active: statuses.length > 0 })}
          options={ACTION_PLAN_STATUS_OPTIONS}
          defaultValue={statuses}
          onSelect={(values: SelectAdvancedFilterOptionType[]) =>
            onStatusesChange(values.map((item) => Number(item.value)))
          }
          placeholder="Status"
          dataTestId="action-plans-filter-status"
        />
        <ResetButton type="button" onClick={onReset} data-test-id="action-plans-filters-reset">
          Reset
        </ResetButton>
      </FiltersRow>
    </Container>
  );
};

export default ActionPlansFilters;
